import { Dashboard } from "../domain/Dashboard";
import { DashboardRepo } from "../domain/DashboardRepo";
import { EntityID, MonthlyKpis, GastosIngresos, UltimasFacturas, ClientesProveedores, PygGrupo, PygData } from "../domain/models";
import { getEntityByType } from "../domain/EntityOperations";
import { QueryDispatcher } from "@/modules/shared/domain/QueryDispatcher";
import store from "@/store";

export class ApiDashboardRepository implements DashboardRepo {
  queryDispatcher: QueryDispatcher;
  userId: string;
  companyId: number;

  constructor(queryDispatcher: QueryDispatcher, userId: string, companyId: number) {
    this.queryDispatcher = queryDispatcher;
    this.userId = userId;
    this.companyId = companyId;
  }

  async loadPygData(): Promise<{ pygGrupos: PygGrupo[], pygData: PygData[] }> {
    try {
      // Fetch pygGrupos data
      const pygGruposData = await getEntityByType(
        EntityID.PYG_GRUPOS,
        this.userId,
        this.companyId,
        this.queryDispatcher
      );
      // console.log(`P&G Grupos data fetched: ${pygGruposData.length} items`);

      // Fetch pygData
      const pygDataResponse = await getEntityByType(
        EntityID.PYG_DATA,
        this.userId,
        this.companyId,
        this.queryDispatcher
      );
      // console.log(`P&G Data fetched: ${pygDataResponse.length} items`);

      // Process pygGrupos data
      const pygGrupos: PygGrupo[] = pygGruposData.map(item => item.payload);

      // Process pygData
      const pygData: PygData[] = pygDataResponse.map(item => item.payload);

      // Log first few items to understand data structure
      // console.log('First 3 pygGrupos:', JSON.stringify(pygGrupos.slice(0, 3), null, 2));
      // console.log('First 5 pygData items:', JSON.stringify(pygData.slice(0, 5), null, 2));

      return { pygGrupos, pygData };
    } catch (error) {
      console.error("Error loading P&G data:", error);
      throw error;
    }
  }

  // Method to map document state to a user-friendly format
  mapDocumentState(state: string): string {
    const stateMap: {[key: string]: string} = {
      'done': 'Contabilizada',
      'pending_sync': 'Pendiente de sincronizar',
      'pending_review': 'Pendiente de revisión',
      'pending_connect': 'Contabilizando',
      'accounting': 'Contabilizando...',
      'error_account_connector': 'Error de contabilización',
      'error_already_destination': 'Error: Ya existente en destino',
      'pending_upload': 'Pendiente de carga',
      'error_critical': 'Error crítico'
    };
    
    return stateMap[state] || 'Pendiente de contabilizar';
  }

  async loadDashboard(): Promise<Dashboard> {
    try {
      // Fetch monthlyKpis data
      const monthlyKpisData = await getEntityByType(
        EntityID.MONTHLY_KPIS,
        this.userId,
        this.companyId,
        this.queryDispatcher
      );
      console.log(`Monthly KPIs data fetched: ${monthlyKpisData.length} items`);
      console.log(monthlyKpisData)
      

      // Fetch gastosIngresos data
      const gastosIngresosData = await getEntityByType(
        EntityID.GASTO_INGRESO,
        this.userId,
        this.companyId,
        this.queryDispatcher
      );
      console.log(`Gastos e Ingresos data fetched: ${gastosIngresosData.length} items`);

      // Fetch clientesProveedores data
      const clientesProveedoresData = await getEntityByType(
        EntityID.CLIENTE_PROVEEDOR,
        this.userId,
        this.companyId,
        this.queryDispatcher
      );
      console.log(`Clientes y Proveedores data fetched: ${clientesProveedoresData.length} items`);


      // Process monthlyKpis data
      const monthlyKpis: MonthlyKpis[] = monthlyKpisData.map(item => item.payload);
      
      // Sort monthlyKpis by mes_ejercicio in ascending order
      monthlyKpis.sort((a, b) => {
        // Parse dates in format MM/YYYY
        const [monthA, yearA] = a.mes_ejercicio.split('/').map(Number);
        const [monthB, yearB] = b.mes_ejercicio.split('/').map(Number);
        
        // Compare years first
        if (yearA !== yearB) {
          return yearA - yearB;
        }
        
        // If years are the same, compare months
        return monthA - monthB;
      });

      // Process gastosIngresos data
      const gastosIngresos: GastosIngresos[] = gastosIngresosData.map(item => item.payload);
      // const gastosIngresos: GastosIngresos[] = []

      // Process clientesProveedores data
      const clientesProveedores: ClientesProveedores[] = clientesProveedoresData.map(item => item.payload);
      // Get documents from the store
      const documents = store.getters['documents/currentDocuments'] || [];
      
      const ultimasFacturas: UltimasFacturas[] = [...documents]
          .sort((a, b) => b.id - a.id)
          .slice(0, 20)
          .map(doc => ({
            id: doc.id,
            fecha: doc.created,
            origen: doc.third_name || "Desconocido",
            tipo: doc.kind_display,
            importe: doc.total || 0,
            estado: this.mapDocumentState(doc.state)
      }));

      // Create the dashboardData object
      const dashboardData = {
        monthlyKpis,
        gastosIngresos,
        clientesProveedores,
        ultimasFacturas,
      };

      return new Dashboard("Dashboard", "Este es el Dashboard", dashboardData);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      throw error;
    }
  }
}